// src/components/Footer.js
import React, { useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, ExternalLink } from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  
  // Determine if we're in the dashboard layout
  const isDashboardLayout = location.pathname.includes('/dashboard') || 
                            location.pathname.includes('/profile') || 
                            location.pathname.includes('/record') ||
                            location.pathname.includes('/admin');

  // Helper function for smooth scrolling
  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Use a lighter footer for dashboard pages to match the new design
  if (isDashboardLayout) {
    return (
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} ReelIntro. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a 
                href="https://linkedin.com/in/yourprofile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com/yourprofile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <Link 
                to="/legal/terms-of-service"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Terms
              </Link>
              <Link 
                to="/legal/privacy-policy"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Privacy
              </Link>
              <Link 
                to="/blog"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Blog
              </Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Original footer for public pages
  return (
    <footer className="bg-gray-800 text-gray-200">
      <div className="container mx-auto px-4 py-12">
        {/* Top Section */}
        <div className="flex flex-col md:flex-row justify-between">
          {/* Brand and Description */}
          <div className="mb-8 md:mb-0">
            <Link to="/" className="text-2xl font-bold text-white hover:text-gray-400 transition-colors duration-200">
              ReelIntro
            </Link>
            <p className="mt-4 text-gray-400">
              Empowering job seekers to make memorable impressions with professional video introductions.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="flex space-x-12">
            <div>
              <h4 className="font-semibold text-white mb-4">Navigate</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('pricing')}
                    className="hover:text-white transition-colors duration-200"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('faq')}
                    className="hover:text-white transition-colors duration-200"
                  >
                    FAQ
                  </button>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-white transition-colors duration-200">
                    Blog
                  </Link>
                </li>
                {currentUser && (
                  <li>
                    <Link to="/dashboard/home" className="hover:text-white transition-colors duration-200 flex items-center">
                      Dashboard <ExternalLink className="ml-1 w-3 h-3" />
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/legal/terms-of-service" className="hover:text-white transition-colors duration-200">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/legal/privacy-policy" className="hover:text-white transition-colors duration-200">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 border-t border-gray-700"></div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          {/* Copyright */}
          <p className="text-gray-500">
            &copy; {new Date().getFullYear()} ReelIntro. All rights reserved.
          </p>

          {/* Social Media Icons */}
          <div className="flex space-x-4 mt-4 md:mt-0">
            <a
              href="https://facebook.com/yourprofile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://twitter.com/yourprofile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Twitter className="w-6 h-6" />
            </a>
            <a
              href="https://linkedin.com/in/yourprofile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Linkedin className="w-6 h-6" />
            </a>
            <a
              href="https://instagram.com/yourprofile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
